import { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Twitter, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { publicApi } from '../lib/api';

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getSettings()
      .then(res => setSettings(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const contact = settings?.contact || {};
  const social = settings?.social_links || {};
  const whatsappNumber = contact.whatsapp?.replace(/\D/g, '') || '18091234567';
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, tengo una consulta sobre sus excursiones.')}`;

  const socialLinks = [
    { icon: Instagram, url: social.instagram, label: 'Instagram', color: 'hover:bg-pink-500' },
    { icon: Facebook, url: social.facebook, label: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: Youtube, url: social.youtube, label: 'YouTube', color: 'hover:bg-red-600' },
    { icon: Twitter, url: social.twitter, label: 'Twitter', color: 'hover:bg-sky-500' },
  ].filter(s => s.url);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="contact-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">
            Contacto
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-4">
            ¿Tienes Preguntas?
          </h1>
          <p className="text-stone-500 max-w-2xl mx-auto">
            Estamos aquí para ayudarte. Contáctanos por WhatsApp para una respuesta 
            rápida o usa cualquiera de nuestros canales de comunicación.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* WhatsApp Card */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#25D366]/10 flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-[#25D366]" />
              </div>
              <h3 className="font-serif text-xl text-stone-900 mb-2">WhatsApp</h3>
              <p className="text-stone-500 mb-4">Respuesta en minutos</p>
              <p className="text-lg font-semibold text-stone-700 mb-4">
                {contact.whatsapp || '+1 809 123 4567'}
              </p>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button className="rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2">
                  <Phone className="w-4 h-4" />
                  Enviar Mensaje
                </Button>
              </a>
            </div>

            {/* Email Card */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-serif text-xl text-stone-900 mb-2">Email</h3>
              <p className="text-stone-500 mb-4">Para consultas formales</p>
              <p className="text-lg font-semibold text-stone-700 mb-4">
                {contact.email || 'info@caribbeangroovetours.com'}
              </p>
              <a href={`mailto:${contact.email || 'info@caribbeangroovetours.com'}`}>
                <Button variant="outline" className="rounded-xl border-primary text-primary hover:bg-primary hover:text-white gap-2">
                  <Mail className="w-4 h-4" />
                  Enviar Email
                </Button>
              </a>
            </div>
          </div>

          {/* Location */}
          <div className="mt-6 bg-white rounded-3xl p-8 shadow-sm border border-stone-100">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-8 h-8 text-secondary" />
              </div>
              <div className="text-center md:text-left flex-1">
                <h3 className="font-serif text-xl text-stone-900 mb-2">Ubicación</h3>
                <p className="text-stone-500">
                  {contact.address || 'República Dominicana'}
                </p>
                <p className="text-sm text-stone-400 mt-1">
                  Operamos en las principales zonas turísticas: Punta Cana, La Romana, 
                  Puerto Plata, Samaná y más.
                </p>
              </div>
            </div>
          </div>

          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className="mt-8 text-center">
              <h3 className="font-serif text-xl text-stone-900 mb-4">Síguenos</h3>
              <div className="flex justify-center gap-4">
                {socialLinks.map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center text-stone-600 transition-all ${s.color} hover:text-white`}
                    aria-label={s.label}
                  >
                    <s.icon className="w-6 h-6" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
            ¿Listo para reservar?
          </h2>
          <p className="text-white/80 mb-8">
            No esperes más, las mejores excursiones del Caribe te esperan.
          </p>
          <a href="/reservar">
            <Button 
              size="lg"
              className="rounded-full bg-white text-primary hover:bg-white/90 px-10"
            >
              Ir a Reservaciones
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
